"use client";

import React, { useEffect, useRef } from "react";
import { HelperMessage } from "../text-field/helper-message";
import { CHEVRON_DOWN_GLYPH_PATH, CHEVRON_DOWN_GLYPH_VIEWBOX } from "../chevron-down-glyph";
import styles from "./dropdown.module.css";
import { withSafeRender } from "../safe-render";

/**
 * Dropdown — a labelled select matching Eclipse "Dropdown"
 * (`5cae8dfcb6053773e1bc4f7d6d304fcc965fef82`). A native `<select>`: "prefer the
 * platform" (CLAUDE.md) — the browser owns the open/close, the keyboard map and the
 * option list's accessibility tree.
 *
 * "use client": `focusOnError` is an effect on a ref, and the value goes out through
 * `onValueChange` — data out, not a DOM event. Argued in `design-conflicts/zip-code.mdx`.
 */
export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps {
  /** Ties the label and message to the control; must be unique on the page. */
  id: string;
  /** The visible label, always the 12px line above the displayed value, inside the box. Also its accessible name. */
  label: string;
  /** The selected option's value, or `""` for none selected. Controlled. */
  value: string;
  /** Fires with the newly selected option's value. */
  onValueChange: (value: string) => void;
  /** The choices, in order. An empty list still renders the field — it simply has nothing to open. */
  options: DropdownOption[];
  /** Shown as the unselectable first option while `value` is `""`. */
  placeholder?: string;
  /** Shows the error treatment and switches the message row to `errorMessage`. */
  error?: boolean;
  /** Shown instead of `helperMessage` while `error` is true. */
  errorMessage?: string;
  /** Shown under the control while there is no error. */
  helperMessage?: string;
  disabled?: boolean;
  /** The choices are still being fetched: announces `aria-busy`. */
  loading?: boolean;
  /** Shows the value as read-only text in the same box, without the chevron — for a one-choice list. A read-only textbox rather than a select, so it is not offered as operable; not `disabled`, so it keeps full contrast. */
  locked?: boolean;
  /** Moves focus to the control the moment `error` turns on, so a reader who just pressed submit is taken to what needs fixing; the message is then not also announced as an alert. Off by default. ⚠ While `disabled`, focus cannot move, so the message announces as an alert instead — the error is never silent. */
  focusOnError?: boolean;
  className?: string;
}

function DropdownImpl({
  id,
  label,
  value,
  onValueChange,
  options,
  placeholder,
  error = false,
  errorMessage,
  helperMessage,
  disabled = false,
  loading = false,
  locked = false,
  focusOnError = false,
  className
}: DropdownProps) {
  /* Whichever control is on screen: `locked` swaps the select for a readOnly input,
     and a ref bound to the select alone would make focus-on-error a silent no-op
     there — the reader is told nothing and the focus never moves. */
  const controlRef = useRef<HTMLSelectElement | HTMLInputElement>(null);
  const wasErrorRef = useRef(false);

  /* `focusOnError` trades the alert for a focus move. A DISABLED select cannot take
     focus — per the HTML spec and in jsdom — so the trade has nothing to trade: the
     `focus()` below is a silent no-op AND `announce={false}` suppresses the alert,
     leaving the message never conveyed at all. One flag drives both, so they cannot
     drift apart.

     `locked` is deliberately NOT swept in here: it swaps in a readOnly INPUT, which
     is still focusable, so the focus move still works and the alert stays off. */
  const focusMovesOnError = focusOnError && !disabled;

  useEffect(() => {
    if (focusMovesOnError && error && !wasErrorRef.current) {
      controlRef.current?.focus();
    }
    /* Tracked even when focus cannot move, so a later enable while the error is still
       on does not steal focus — it only drops the alert role, and removing a role does
       not re-announce. */
    wasErrorRef.current = error;
  }, [error, focusMovesOnError]);

  /* A value no option carries ("" or a stale one) would paint the FIRST option as
     selected while the state says none. It is read as "" instead, and selects the
     placeholder, or a hidden, disabled empty option when there is none. */
  const hasMatch = options.some(option => option.value === value);
  const selectValue = hasMatch ? value : "";
  const needsEmptyOption = !hasMatch && placeholder === undefined;
  const messageId = `${id}-message`;
  const message = error ? errorMessage : helperMessage;
  const hasMessage = !(message === undefined || message === null || message === "");

  return (
    <div className={[styles.root, className].filter(Boolean).join(" ")} data-empty={hasMatch ? undefined : ""}>
      <div className={styles.selectWrap}>
        {locked ? (
          <input
            aria-describedby={hasMessage ? messageId : undefined}
            aria-invalid={error || undefined}
            className={[styles.select, styles.selectLocked, error ? styles.selectError : null].filter(Boolean).join(" ")}
            id={id}
            readOnly
            ref={controlRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={options.find(option => option.value === selectValue)?.label ?? ""}
          />
        ) : (
          <select
            aria-describedby={hasMessage ? messageId : undefined}
            aria-busy={loading || undefined}
            aria-invalid={error || undefined}
            className={[styles.select, error ? styles.selectError : null].filter(Boolean).join(" ")}
            disabled={disabled}
            id={id}
            onChange={event => onValueChange(event.target.value)}
            ref={controlRef as React.RefObject<HTMLSelectElement>}
            value={selectValue}
          >
            {needsEmptyOption && <option disabled hidden value="" />}
            {placeholder !== undefined && (
              <option disabled hidden value="">
                {placeholder}
              </option>
            )}
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {!locked && (
          <svg
            aria-hidden="true"
            className={styles.chevron}
            focusable="false"
            viewBox={CHEVRON_DOWN_GLYPH_VIEWBOX}
          >
            <path d={CHEVRON_DOWN_GLYPH_PATH} fill="currentColor" />
          </svg>
        )}
      </div>
      {hasMessage && (
        <HelperMessage announce={!focusMovesOnError} id={messageId} tone={error ? "error" : disabled ? "disabled" : "helper"}>
          {message}
        </HelperMessage>
      )}
    </div>
  );
}
export const Dropdown = withSafeRender(DropdownImpl, "Dropdown");

export default Dropdown;
